import { createNode, emptyScope, metaForKind } from "./catalog";
import type {
  AutomationEdge,
  AutomationKind,
  AutomationNode,
  FillFormConfig,
  HospitalAutomation,
  ListenConfig,
  NotifyAgenciesConfig,
  NotifyEmailConfig,
  NotifySmsConfig,
  ShiftStartsWithinConfig,
} from "./types";

export const NODE_WIDTH = 300;

export const LANE_COLUMNS: Record<string, number> = {
  listen: 0,
  collect: 1,
  condition: 2,
  decide: 3,
  notify: 4,
};

export function nextPosition(nodes: AutomationNode[], kind: AutomationKind) {
  const lane = metaForKind(kind).lane;
  const column = LANE_COLUMNS[lane] ?? 0;
  const inLane = nodes.filter((node) => node.lane === lane);
  return {
    x: 80 + column * (NODE_WIDTH + 140),
    y: 80 + inLane.length * 180,
  };
}

export function wouldCycle(
  edges: AutomationEdge[],
  source: string,
  target: string,
) {
  if (source === target) return true;
  const outgoing = new Map<string, string[]>();
  edges.forEach((edge) => {
    const list = outgoing.get(edge.source) || [];
    list.push(edge.target);
    outgoing.set(edge.source, list);
  });
  const stack = [target];
  const seen = new Set<string>();
  while (stack.length) {
    const current = stack.pop() as string;
    if (current === source) return true;
    if (seen.has(current)) continue;
    seen.add(current);
    (outgoing.get(current) || []).forEach((next) => stack.push(next));
  }
  return false;
}

export function canConnect(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  sourceId: string,
  targetId: string,
) {
  const source = nodes.find((node) => node.id === sourceId);
  const target = nodes.find((node) => node.id === targetId);
  if (!source || !target) return false;
  if (target.lane === "listen") return false;
  if (sourceId === targetId) return false;
  return !wouldCycle(edges, sourceId, targetId);
}

function asFillForm(config: Record<string, any>): FillFormConfig {
  const guides = Array.isArray(config.guides) ? config.guides : [];
  if (!guides.length && config.guideId) {
    return {
      guides: [{ guideId: config.guideId, guideName: config.guideName || "" }],
      requireVerified: !!config.requireVerified,
      optional: !!config.optional,
    };
  }
  return {
    guides,
    requireVerified: !!config.requireVerified,
    optional: !!config.optional,
  };
}

function resolveKind(kind: AutomationKind): AutomationKind {
  if (kind === "collect.send_sms") return "collect.conversation";
  return kind;
}

function notifyTargets(config: Record<string, any>) {
  if (typeof config.hospital === "boolean" || typeof config.doctor === "boolean") {
    return { hospital: !!config.hospital, doctor: !!config.doctor };
  }
  if (config.audience === "doctor") return { hospital: false, doctor: true };
  if (config.audience === "both") return { hospital: true, doctor: true };
  return { hospital: true, doctor: false };
}

function asNotifyEmail(config: Record<string, any>): NotifyEmailConfig {
  const targets = notifyTargets(config);
  return {
    hospital: targets.hospital,
    doctor: targets.doctor,
    subject: config.subject || "",
    body: config.body || "",
    optional: !!config.optional,
  };
}

function asNotifySms(config: Record<string, any>): NotifySmsConfig {
  const targets = notifyTargets(config);
  return {
    hospital: targets.hospital,
    doctor: targets.doctor,
    message: config.message || "",
    optional: !!config.optional,
  };
}

function parseEmails(value: unknown): string[] {
  const raw = Array.isArray(value)
    ? value.join("\n")
    : typeof value === "string"
      ? value
      : "";
  return raw
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter((item) => item.includes("@"));
}

function asNotifyAgencies(config: Record<string, any>): NotifyAgenciesConfig {
  const rawSubject = String(config.subject || "");
  const rawBody = String(config.body || "");
  const staleSubject = !rawSubject || /new locum shift posted/i.test(rawSubject);
  const staleBody = !rawBody || /StatHospital/i.test(rawBody);
  return {
    emails: parseEmails(config.emails),
    subject: staleSubject ? "New locum doctors needed" : rawSubject,
    body: staleBody
      ? "New locum doctors needed.\n\n{{openDates}}\nSpecialty: {{specialty}}\nSkill level: {{skillLevel}}"
      : rawBody,
    optional: !!config.optional,
  };
}

function asShiftStartsWithin(config: Record<string, any>): ShiftStartsWithinConfig {
  return {
    days: Math.max(1, Number(config.days) || 7),
    scope: "group_first",
  };
}

export function normalizeNode(
  node: AutomationNode,
  index: number,
): AutomationNode {
  const kind = resolveKind(node.kind);
  const listen = node.config as ListenConfig;
  const scope = node.scope || {
    specialtyIds: Array.isArray(listen?.specialtyIds) ? listen.specialtyIds : [],
    minimumSkillId: listen?.minimumSkillId,
  };
  const rawConfig = (node.config || {}) as Record<string, any>;
  const config =
    kind === "collect.fill_form"
      ? asFillForm(rawConfig)
      : kind === "notify.email"
        ? asNotifyEmail(rawConfig)
        : kind === "notify.sms"
          ? asNotifySms(rawConfig)
          : kind === "notify.agencies"
            ? asNotifyAgencies(rawConfig)
            : kind === "condition.shift_starts_within"
              ? asShiftStartsWithin(rawConfig)
              : rawConfig;
  return {
    ...node,
    kind,
    lane: metaForKind(kind).lane,
    config,
    scope: {
      specialtyIds: Array.isArray(scope.specialtyIds) ? scope.specialtyIds : [],
      minimumSkillId: scope.minimumSkillId,
    },
    position: node.position || {
      x: 72 + (index % 4) * (NODE_WIDTH + 88),
      y: 72 + Math.floor(index / 4) * 140,
    },
  };
}

export function normalizeAutomation(
  automation: HospitalAutomation,
): HospitalAutomation {
  return {
    ...automation,
    nodes: (automation.nodes || []).map(normalizeNode),
    edges: Array.isArray(automation.edges) ? automation.edges : [],
  };
}

export function createPlacedNode(
  kind: AutomationKind,
  nodes: AutomationNode[],
): AutomationNode {
  return {
    ...createNode(kind),
    position: nextPosition(nodes, kind),
  };
}

export { emptyScope };
